/* eslint-disable react/prop-types */
import React, { useEffect, useRef, useState } from "react";
import clsx from "clsx";
import PropTypes from "prop-types";
import { Popover } from "@mui/material";
import AutoSizer from "react-virtualized/dist/commonjs/AutoSizer";
import { InfiniteLoader, List, WindowScroller } from "react-virtualized";

import { NotFound, Button, Loader } from "src/components";

import { TableRow } from "./TableRow";
import { TableHead } from "./TableHead";
import { columnsPropType } from "./types";
import { SelectionMenu } from "./SelectionMenu";
import { commonVaribles } from 'src/constants/constVariables';

import styles from "./Table.module.scss";
import { isPlainObject } from "@reduxjs/toolkit";

function filterBySearchTexts(fieldToSearchTextMap, data, columns) {
  const fieldToSearchFnMap = columns.reduce((acc, col) => {
    if (col.searchFn) return { ...acc, [col.field]: col.searchFn };
    return acc;
  }, {});

  let filteredData = [...data];
  Object.entries(fieldToSearchTextMap).forEach((entry) => {
    const [field, searchText] = entry;
    const defaultSearchFn = (fieldValue, searchValue) =>
      fieldValue.toLowerCase().startsWith(searchValue);

    const searchFn = fieldToSearchFnMap[field] || defaultSearchFn;

    filteredData = filteredData.filter(
      (row) => !searchText || !row[field] || searchFn(row[field], searchText)
    );
  });
  return filteredData;
}

function filterBySelectedFilters(fieldToFiltersSetMap, data, columns) {
  const fieldToFilterFnMap = columns.reduce((acc, col) => {
    if (col.filterFn) return { ...acc, [col.field]: col.filterFn };
    return acc;
  }, {});

  let filteredData = [...data];
  Object.entries(fieldToFiltersSetMap).forEach((entry, idx) => {
    const [field, filtersSet] = entry;
    const filterFn = fieldToFilterFnMap[field];

    filteredData = filteredData.filter((row) => {
      if (!filterFn) {
        return (
          !filtersSet?.size ||
          (isPlainObject(row[field]) ? filtersSet.has(row[field]?.props?.rowData[field]) : filtersSet.has(row[field])) ||
          (row[field] instanceof Array &&
            row[field].some((i) => [...filtersSet].includes(i)))
        );
      }
      if (!idx) {
      }

      return (
        !filtersSet?.size ||
        [...filtersSet].some((filter) =>
          filterFn(row, filter.value ? filter.value : filter)
        )
      );
    });
  });
  return filteredData;
}

function deepCompareEqualsColumnsArray(oldArray, newArray) {
  if (oldArray?.length !== newArray?.length) return false;
  const zippedArray = oldArray.map((e, i) => [e, newArray[i]]);
  return zippedArray.every((tuple) => tuple[0].title === tuple[1].title);
}

function useDeepCompareColumnArraysMemoize(value) {
  const ref = useRef();
  if (!deepCompareEqualsColumnsArray(value, ref.current)) {
    ref.current = value;
  }

  return ref.current;
}

function useDeepCompareColumnArraysEffect(callback, dependencies) {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(callback, dependencies.map(useDeepCompareColumnArraysMemoize));
}

const primaryActionsEnable = [
  "Add NEW TROLLEY",
  "Create Logistics Form",
  "ADD",
  "Bulk data",
  "RESET PART FAMILY",
  "Upload Image",
  "Regenerate Sequence",
  "Upload Logistics Sheet"
];

export const Table = ({
  rows: data,
  columns: originalColumns,
  classNames = {},
  rowHeight = 38,
  headerHeight,
  defaultColumns: originalDefaultColumns,
  removeRowSelection,
  primaryActions = [],
  secondaryActions = [],
  setHasActiveEdits,
  setAsnCount,
  isDataLoading,
  showRadioButton,
  onSelect,
  tableName,
  pagination,
  infiniteScroll,
  onRowSelect,
  loadMoreData,
  onClickPath,
  handleSingleCheckbox,
  handleCheckbox,
  selectCount,
  setTotalData,
  selectedRows,
  updateSetSelectedRows,
  isModal,
  isAlertTable,
  buttonClicked,
  onChangeFilterResetTheCheckbox,
  searchMessage,
  searchQuery
}) => {
  const [rowsData, setRowsData] = useState({
    data: [...data],
    filteredData: [...data],
  });
  const [fieldToSearchTextMap, setFieldToSearchTextMap] = useState({});
  const [isEllipsisPopupOpen, setIsEllipsisPopupOpen] = useState(false);
  const [ellipsisAnchorEl, setEllipsisAnchorEl] = useState(null);
  const [ellipsisPopupValue, setEllipsisPopupValue] = useState(null);
  const [fieldToFiltersSetMap, setFieldToFiltersSetMap] = useState({});
  const butonRef = useRef(0);
  const closeEllipsisPopup = () => setIsEllipsisPopupOpen(false);

  const vListRef = useRef(null);
  const tableHeadRef = useRef(null);

  const handleListScroll = (e) => {
    tableHeadRef.current.scrollLeft = e.target.scrollLeft;
  };

  const syncListToTableHead = (e) => {
    vListRef.current.scrollLeft = e.target.scrollLeft;
  };

  useEffect(() => {
    if (isModal) {
      vListRef.current = document.querySelector(
        ".ReactVirtualized__Grid.ReactVirtualized__List.popUpModalTable"
      );
    } else {
      vListRef.current = document.querySelector(
        ".ReactVirtualized__Grid.ReactVirtualized__List"
      );
    }
    if (vListRef.current && tableHeadRef.current) {
      vListRef.current.addEventListener("scroll", handleListScroll);
      tableHeadRef.current.addEventListener("scroll", syncListToTableHead);
    }
  }, [rowsData]);

  useEffect(() => {
    // TODO: Fix this
    if (setAsnCount) {
      setAsnCount(rowsData.filteredData.length);
    }
  }, [setAsnCount, rowsData]);

  const handlePopoverOpen = (event, val) => {
    if (event.currentTarget.scrollWidth > event.currentTarget.clientWidth) {
      setEllipsisPopupValue(val);
      setIsEllipsisPopupOpen(true);
      setEllipsisAnchorEl(event.currentTarget);
    }
  };

  const handlePopoverClose = () => {
    setIsEllipsisPopupOpen(false);
    setEllipsisAnchorEl(null);
  };

  const toggleSelectAllRows = () => {
    var ab = [];
    setRowsData((prev) => {
      const visibleRowIdsSet = new Set(
        prev.filteredData.map((row) => row.passive_id)
      );
      const isSelected = !prev.filteredData.every((row) => row.isSelected);
      ab.push(...visibleRowIdsSet);
      return {
        ...prev,
        data: prev.data.map((row) => ({ ...row, isSelected })),
      };
    });
    updateSetSelectedRows("all", rowsData.data, ab);
    handleCheckbox();
  };

  const handleRowSelection = (selectedId) => {
    setRowsData((prev) => ({
      ...prev,
      data: prev.data.map((row) =>
        row.passive_id === selectedId
          ? { ...row, isSelected: !row.isSelected }
          : row
      ),
    }));
    if (updateSetSelectedRows) {
      updateSetSelectedRows(selectedId);
    }
    handleSingleCheckbox(selectedId);
  };

  const handleRadioRowSelection = (item) => {
    setRowsData((prev) => ({
      ...prev,
      data: prev.data.map((row) =>
        row.passive_id === item.id
          ? { ...row, isSelected: true }
          : { ...row, isSelected: false }
      ),
    }));
    onSelect(item);
  };

  useEffect(() => {
    if (setHasActiveEdits) {
      const areEditsActive = rowsData.data.some((row) => row.isSelected);
      setHasActiveEdits(areEditsActive);
    }
  }, [rowsData.data, setHasActiveEdits]);

  useEffect(() => {
    setTotalData(rowsData?.filteredData?.length);
  }, [rowsData]);
  const calcWidth = (value) => {
    var canvas = document.createElement("canvas");
    var ctx = canvas.getContext("2d");
    ctx.font = "15px sans-serif"; // This can be set programmaticly from the element's font-style if desired
    var textWidth = ctx.measureText(value).width;
  };
  calcWidth(String(rowsData.data.length).length);
  const defaultColumns = [
    showRadioButton
      ? null
      : {
          title: "#",
          field: "_serialNumber",
          width:
            String(rowsData.data.length).length > 1
              ? String(rowsData.data.length).length * 10 + 5
              : 22,
          render: (_, idx) => idx,
          headerStyles: {
            paddingLeft: 7,
            paddingRight: 2,
          },
        },
    (primaryActions || secondaryActions?.length) &&
    !removeRowSelection &&
    !isModal &&
    !isAlertTable
      ? {
          title: "select",
          field: "_select",
          width: 30,
          renderHeader: (rows) => (
            <input
              type="checkbox"
              onChange={toggleSelectAllRows}
              checked={
                rows?.length &&
                selectedRows?.length &&
                rows.every((item) => item.isSelected === true)
              }
            />
          ),
          render: ({ passive_id }) => (
            <input
              style={{ marginTop: 0, marginBottom: 0 }}
              type="checkbox"
              onChange={() => handleRowSelection(passive_id)}
              checked={selectedRows ? selectedRows[passive_id] : false}
              data-testid={`select-row-${passive_id}`}
              id={`select-row-${passive_id}`}
              name={`select-row-${passive_id}`}
            />
          ),
        }
      : null,

    ...originalDefaultColumns,
  ].filter((i) => i);
  const radioColumn = [
    showRadioButton
      ? {
          title: "select",
          field: "_select",
          width: 30,
          renderHeader: (rows) => (
            <input
              type="radio"
              onChange={toggleSelectAllRows}
              checked={
                rows?.length &&
                selectedRows?.length &&
                selectedRows.every((row) => row)
              }
            />
          ),
          render: (row) => (
            <input
              style={{ marginTop: 0, marginBottom: 0 }}
              type="radio"
              onChange={() => handleRadioRowSelection(row)}
              checked={selectedRows ? selectedRows[row.passive_id] : false}
              data-testid={`radio-row-${row.passive_id}`}
              id={`radio-row-${row.passive_id}`}
              name={`radio-row-${tableName}`}
            />
          ),
        }
      : null,
  ];
  const [selectedColumns, setSelectedColumns] = useState(defaultColumns);

  const noOfFixedColumns = primaryActions || secondaryActions?.length ? 2 : 1;

  const [isCustomizeTableMenuOpen, setIsCustomizeTableMenuOpen] =
    useState(false);
  const [showCopyAlert, setShowCopyAlert] = useState(false);

  const customizeTableButtonRef = useRef(null);

  const reorder = (list, startIndex, endIndex) => {
    const result = [...list];
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    return result;
  };

  const reorderHeaders = (result) => {
    if (!result.destination || result.destination.index < noOfFixedColumns) {
      return;
    }

    const reorderedColumns = reorder(
      selectedColumns,
      result.source.index,
      result.destination.index
    );

    setSelectedColumns(reorderedColumns);
  };

  function checkBooleanArrayChecedstate() {
    if (selectedRows?.some(Boolean)) {
      if (selectedRows.filter((value) => value).length === 1) {
        return false;
      } else {
        return true;
      }
    } else {
      return true;
    }
  }

  const rowRenderer = ({ style, index, key }) => {
    return (
      <TableRow
        index={index}
        style={style}
        key={key}
        selectedRows={selectedRows}
        row={rowsData.filteredData[index]}
        columns={
          showRadioButton
            ? [...radioColumn, ...selectedColumns]
            : [...selectedColumns]
        }
        classNames={{ ...classNames }}
        rowHeight={rowHeight}
        handlePopoverOpen={handlePopoverOpen}
        handlePopoverClose={handlePopoverClose}
        onClickPath={onClickPath}
        rowClick={(e) => (onRowSelect ? onRowSelect(rowsData.filteredData[index]) : {})}
        searchText={searchQuery}
      />
    );
  };

  const handleColumnSelectionChange = (columnTitle) => {
    if (selectedColumns.map(({ title }) => title).includes(columnTitle))
      return setSelectedColumns((prev) =>
        prev.filter((column) => column.title !== columnTitle)
      );
    return setSelectedColumns((prev) => [
      ...prev,
      ...originalColumns.filter(({ title }) => title === columnTitle),
    ]);
  };

  const applyColumnsSelections = (columnSelections) => {
    setSelectedColumns([
      ...defaultColumns.slice(
        0,
        defaultColumns.length - originalDefaultColumns?.length
      ),
      ...originalColumns.filter((col) => columnSelections.includes(col.title)),
    ]);
  };

  useEffect(() => {
    if (showCopyAlert) setShowCopyAlert(false);
  }, [showCopyAlert]);
  useDeepCompareColumnArraysEffect(() => {
    setSelectedColumns(defaultColumns);
  }, [defaultColumns]);

  useEffect(() => {
    setSelectedColumns(defaultColumns);
  }, [selectedRows]);

  useEffect(() => {
    onChangeFilterResetTheCheckbox();
    setRowsData({
      data: [...data],
      filteredData: [...data],
    });
  }, [fieldToFiltersSetMap]);

  useEffect(() => {
    setRowsData((prevState) => {
      const dataFilteredBySearchTexts = filterBySearchTexts(
        fieldToSearchTextMap,
        prevState.data,
        selectedColumns
      );

      const dataFilteredBySearchAndFilters = filterBySelectedFilters(
        fieldToFiltersSetMap,
        dataFilteredBySearchTexts,
        selectedColumns
      );

      return { ...prevState, filteredData: dataFilteredBySearchAndFilters };
    });
  }, [
    selectedColumns,
    fieldToFiltersSetMap,
    fieldToSearchTextMap,
    rowsData.data,
  ]);

  useEffect(() => {
    setRowsData({
      data: [...data],
      filteredData: [...data],
    });
  }, [data]);

  const isRowLoaded = ({ index }) => !!rowsData.data[index];

  const loadMoreRows = () =>
    new Promise((resolve) => {
      if (!pagination.last) {
        pagination.setCurrentPageNumber((prev) => ({
          ...prev,
          number: prev.number + 1,
        }));
        resolve();
      } else {
        resolve();
      }
    });

  return (
    <>
      <div
        style={{ width: "100%", height: "100%" }}
        className={clsx(
          styles.baseContainer,
          classNames.tableContainerClassName
        )}
      >
        {rowsData.data.length > 0 ? (
          <div
            style={{ width: "100%" }}
            className={clsx(
              styles.basicTableContainer,
              classNames.tableContainerClassName
            )}
          >
            <>
              {infiniteScroll ? (
                <WindowScroller>
                  {({ height, isScrolling, onChildScroll, scrollTop }) => (
                    <InfiniteLoader
                      isRowLoaded={isRowLoaded}
                      loadMoreRows={loadMoreData}
                      rowCount={rowsData.data.length + 1}
                      threshold={1}
                    >
                      {({ onRowsRendered, registerChild }) => (
                        <AutoSizer>
                          {({ width: windowWidth, height: windowHeight }) => {
                            const width = windowWidth;
                            const height = windowHeight;
                            const listHeight = height - rowHeight - 25;
                            return (
                              <div
                                className={clsx(
                                  styles.basicTable,
                                  classNames.tableClassName
                                )}
                                style={{
                                  height,
                                  width,
                                }}
                              >
                                <div
                                  style={
                                    showRadioButton ? { marginLeft: 38 } : null
                                  }
                                >
                                  <TableHead
                                    columns={selectedColumns}
                                    ref={tableHeadRef}
                                    className={classNames.tableHeadClassName}
                                    height={headerHeight || rowHeight}
                                    reorderHeaders={reorderHeaders}
                                    rows={rowsData.filteredData}
                                    allRows={rowsData.data}
                                    fieldToSearchTextMap={fieldToSearchTextMap}
                                    setSearchTextForTitleCurry={(field) =>
                                      (value) =>
                                        setFieldToSearchTextMap((prev) => ({
                                          ...prev,
                                          [field]: value.toLowerCase(),
                                        }))
                                      }
                                    noOfFixedColumns={noOfFixedColumns}
                                    fieldToFiltersSetMap={fieldToFiltersSetMap}
                                    setFiltersForFieldCurry={(field) =>
                                      (selectedFilters) =>
                                        setFieldToFiltersSetMap((prev) => ({
                                          ...prev,
                                          [field]: new Set(selectedFilters),
                                        }))
                                      }
                                  />
                                </div>
                                <List
                                  ref={registerChild}
                                  containerStyle={{ overflow: "initial" }}
                                  rowRenderer={rowRenderer}
                                  height={listHeight}
                                  rowHeight={rowHeight}
                                  rowCount={rowsData.filteredData.length}
                                  width={width}
                                  style={{ outline: "none !improtant" }}
                                  className={clsx(
                                    styles.scrollBar,
                                    styles.heightAdjustedListScroll
                                  )}
                                  overscanRowCount={10}
                                  onRowsRendered={onRowsRendered}
                                />
                                {isDataLoading && (
                                  <div className={styles["loader-div"]}>
                                    <Loader />
                                  </div>
                                )}
                              </div>
                            );
                          }}
                        </AutoSizer>
                      )}
                    </InfiniteLoader>
                  )}
                </WindowScroller>
              ) : (
                <AutoSizer>
                  {({ width: windowWidth, height: windowHeight }) => {
                    const width = windowWidth;
                    const height = windowHeight;
                    const listHeight = height - rowHeight - 2;
                    return (
                      <div
                        className={clsx(
                          styles.basicTable,
                          classNames.tableClassName
                        )}
                        style={{
                          height,
                          width,
                        }}
                      >
                        <div
                          style={showRadioButton ? { marginLeft: 38 } : null}
                        >
                          <TableHead
                            columns={selectedColumns}
                            ref={tableHeadRef}
                            className={classNames.tableHeadClassName}
                            height={headerHeight || rowHeight}
                            reorderHeaders={reorderHeaders}
                            rows={rowsData.filteredData}
                            allRows={rowsData.data}
                            fieldToSearchTextMap={fieldToSearchTextMap}
                            setSearchTextForTitleCurry={(field) => (value) =>
                              setFieldToSearchTextMap((prev) => ({
                                ...prev,
                                [field]: value.toLowerCase(),
                              }))
                            }
                            noOfFixedColumns={noOfFixedColumns}
                            fieldToFiltersSetMap={fieldToFiltersSetMap}
                            setFiltersForFieldCurry={(field) =>
                              (selectedFilters) =>
                                setFieldToFiltersSetMap((prev) => ({
                                  ...prev,
                                  [field]: new Set(selectedFilters),
                                }))
                              }
                          />
                        </div>
                        <List
                          ref={vListRef}
                          containerStyle={{ overflow: "initial" }}
                          rowRenderer={rowRenderer}
                          height={listHeight}
                          rowHeight={rowHeight}
                          rowCount={rowsData.filteredData.length}
                          width={width}
                          className={clsx(
                            styles.scrollBar,
                            styles.heightAdjustedListScroll,
                            isModal && "popUpModalTable"
                          )}
                          overscanRowCount={10}
                        />
                        {isDataLoading && (
                          <div className={styles["loader-div"]}>
                            <Loader />
                          </div>
                        )}
                      </div>
                    );
                  }}
                </AutoSizer>
              )}
            </>
          </div>
        ) : (
          <NotFound message={searchMessage? searchMessage : commonVaribles.data_not_found} />
        )}

        <div
          className={clsx(`TableButton_inner ${styles.actionsBar}`, {
            [`TableButton_inner ${styles.actionsBarRecnotfound}`]:
              rowsData.data.length === 0,
          })}
        >
          {rowsData.data.length > 0 ? (
            <div className={clsx(styles.buttonContainer)}>
              {!isModal && (
                <Button
                  className={clsx(
                    styles.actionButton,
                    styles.cutomizeTableButton,
                    "smallFont"
                  )}
                  variant="tertiary"
                  onClick={() => setIsCustomizeTableMenuOpen(true)}
                  ref={customizeTableButtonRef}
                >
                  CUSTOMIZE TABLE
                </Button>
              )}
              {secondaryActions.map((action, index) => {
                const { name, actionFn } = action;

                return (
                  action.authOperation && 
                  (
                    <Button
                      key={index}
                      ref={butonRef}
                      variant="primary"
                      className={clsx(
                        styles.actionButton,
                        styles.cutomizeTableButton
                      )}
                      style={{
                        backgroundColor: action.backgroundColor,
                      }}
                      simpleClassName={name}
                      onClick={(e) =>
                        actionFn(
                          rowsData.filteredData.filter((row) => row.isSelected)
                        )
                      }
                      disabled={
                        (action.name === "Update" &&
                          checkBooleanArrayChecedstate()) ||
                        buttonClicked
                          ? true
                          : selectedRows.every((v) => v === false)
                      }
                    >
                      {name}
                    </Button>
                  )
                );
              })}
              {!isModal && (
                <Button
                  className={clsx(
                    styles.actionButton,
                    styles.clearFiltersButton
                  )}
                  variant="secondary"
                  onClick={() => setFieldToFiltersSetMap({})}
                >
                  Clear Filters
                </Button>
              )}
            </div>
          ) : null}
          <div
            className={clsx(styles.secondarybuttonsContainer)}
            style={{ right: "0", bottom: "1rem", display: "flex", gap: "1rem" }}
          >
            {primaryActions.map((action, index) => {
              return (
                action.authOperation &&
                  (
                  <Button
                    ref={butonRef}
                    variant="primary"
                    className={clsx(
                      styles.actionButton,
                      styles.cutomizeTableButton
                    )}
                    style={{
                      backgroundColor: action.backgroundColor,
                    }}
                    simpleClassName={action.name}
                    onClick={() => action.actionFn()}
                    disabled={
                      primaryActionsEnable.includes(action.name)
                        ? false
                        : !selectedRows
                          ? true
                          : selectedRows.every((v) => v === false)
                    }
                  >
                    {action.name}
                  </Button>
                )
              );
            })}
          </div>
        </div>
      </div>
      <SelectionMenu
        anchorEl={customizeTableButtonRef.current}
        open={isCustomizeTableMenuOpen}
        handleClose={() => setIsCustomizeTableMenuOpen(false)}
        options={originalColumns.map(({ title }) => title)}
        currentSelections={selectedColumns
          .filter(({ title }) => title !== "select" && title !== "#")
          .map(({ title }) => title)}
        handleChange={handleColumnSelectionChange}
        resetToDefault={() => setSelectedColumns([...defaultColumns])}
        applySelections={applyColumnsSelections}
      />
      <Popover
        open={isEllipsisPopupOpen}
        anchorEl={ellipsisAnchorEl}
        onClose={closeEllipsisPopup}
        className={styles.popover}
        classes={{
          paper: clsx(styles.popoverPaper, "smallFont"),
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        {ellipsisPopupValue}
      </Popover>
    </>
  );
};

Table.propTypes = {
  classNames: PropTypes.shape({
    tableContainerClassName: PropTypes.string,
    tableClassName: PropTypes.string,
    tableRowClassName: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
    tableHeadClassName: PropTypes.string,
    tableCellClassName: PropTypes.string,
  }),
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
    })
  ),
  columns: columnsPropType,
  rowHeight: PropTypes.number,
  headerHeight: PropTypes.number,
  defaultColumns: columnsPropType,
  removeRowSelection: PropTypes.bool,
  primaryActions: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      actionFn: PropTypes.func.isRequired,
      backgroundColor: PropTypes.string,
    })
  ),
  secondaryActions: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      actionFn: PropTypes.func.isRequired,
      backgroundColor: PropTypes.string,
    })
  ),
  setHasActiveEdits: PropTypes.func,
  setAsnCount: PropTypes.func,
  isDataLoading: PropTypes.bool,
  hideActionButtons: PropTypes.bool,
  showRadioButton: PropTypes.bool,
  onSelect: PropTypes.func,
  tableName: PropTypes.string,
  pagination: PropTypes.any,
  infiniteScroll: PropTypes.any,
  onRowSelect: PropTypes.func,
  hasNext: PropTypes.bool,
  loadMoreData: PropTypes.func,
  get: PropTypes.func,
  handleSingleCheckbox: PropTypes.func,
  handleCheckbox: PropTypes.func,
  onClickPath: PropTypes.string,
  selectCount: PropTypes.number,
  setTotalData: PropTypes.func,
  updateSetSelectedRows: PropTypes.func,
  openUploadFile: PropTypes.func,
  showBulkSearch: PropTypes.bool,
  showBulkSearchMain: PropTypes.func,
  clearBulkSearch: PropTypes.func,
  bulkSearchButton: PropTypes.bool,
  onClick: PropTypes.func,
  onChangeFilterResetTheCheckbox: PropTypes.func,
};
